import { Fragment, useMemo, useState } from 'react';
import type { Message, ToolCall } from '@magic/schemas/messages';
import { Button } from '@resultadosdigitais/tangram-components';
import {
  ChevronDown,
  ChevronUp,
  Icon,
} from '@resultadosdigitais/tangram-react-icons';
import { ErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';

import { WithChildren } from '@exact-magic/react-utils';

const StyledButton = styled(Button)`
  width: fit-content;
`;

export type ToolItemProps = {
  call: ToolCall;
  message: Message;
};

export type Tool = {
  name: string;
  component: (props: ToolItemProps) => JSX.Element | null;
  icon?: Icon;
  expandable?: boolean;
};

interface ExpandableProps {
  tool: Tool;
}

export const Expandable = ({
  children,
  tool,
}: WithChildren<ExpandableProps>) => {
  const [isVisible, setIsVisible] = useState(!tool.expandable);

  return (
    <>
      {isVisible ? children : null}
      {tool.expandable && (
        <StyledButton
          kind={Button.kinds.tertiary}
          size={Button.sizes.sm}
          startIcon={tool.icon ? <tool.icon /> : null}
          endIcon={isVisible ? <ChevronUp /> : <ChevronDown />}
          onClick={async () => {
            setIsVisible(!isVisible);
          }}
        >
          {tool.name}
        </StyledButton>
      )}
    </>
  );
};

type ToolsProps = {
  messages: Message[];
  message: Message;
  tools: Record<string, Tool>;
};

export const Tools = ({ message, messages, tools }: ToolsProps) => {
  const call = messages
    .flatMap((m) => m.toolCalls)
    .filter((c) => !!c)
    .find((c) => c!.id === message.toolCallId);

  const tool = useMemo(() => {
    const name = call?.function.name;
    return name ? tools[name] || null : null;
  }, [call]);

  return call && tool ? (
    <ErrorBoundary
      onError={(error, info) => {
        console.error(error);
        console.log(info);
      }}
      fallback={<Fragment></Fragment>}
    >
      <Expandable tool={tool}>
        {tool?.component?.({ call, message })}
      </Expandable>
    </ErrorBoundary>
  ) : null;
};
